'use client';

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, X, Mail, Loader2 } from 'lucide-react';
import { apiPut, isSuccessResponse } from '@/lib/utils/api-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';

interface EmailOnlyEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  institutionName: string;
  institutionId: number;
  matterId: number;
  correspondenceId?: number;
  emailSubject?: string;
  emailBody?: string;
  onSave?: (emailSubject: string, emailBody: string) => void;
  deceasedName?: string;
  matterReference?: string;
}

export function EmailOnlyEditorDialog({
  open,
  onOpenChange,
  institutionName,
  matterId,
  correspondenceId,
  emailSubject: initialSubject = '',
  emailBody: initialBody = '',
  onSave,
  deceasedName,
  matterReference
}: EmailOnlyEditorDialogProps) {
  const defaultSubject = useMemo(
    () =>
      `Estate of ${deceasedName || '[Deceased Name]'} - Follow-up - ${matterReference || '[Matter Reference]'}`,
    [deceasedName, matterReference]
  );
  const defaultBody = useMemo(
    () =>
      `Dear Sir/Madam,\n\nRE: ${deceasedName || '[Deceased Name]'} (deceased) - Follow-up\n    Matter Reference: ${matterReference || '[Matter Reference]'}\n\nI am following up on my previous correspondence regarding the estate of the above-named deceased.\n\n[Please include follow-up details here]\n\nI would appreciate your response at your earliest convenience.\n\nKind regards,`,
    [deceasedName, matterReference]
  );

  const [userSubject, setUserSubject] = useState<string | null>(null);
  const [userBody, setUserBody] = useState<string | null>(null);
  const subject = userSubject ?? (initialSubject || defaultSubject);
  const body = userBody ?? (initialBody || defaultBody);
  const setSubject = (s: string) => setUserSubject(s);
  const setBody = (b: string) => setUserBody(b);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // If we have a correspondence ID, save to backend
      if (correspondenceId && matterId) {
        const updates: Record<string, unknown> = {
          subject: subject,
          mainContent: body
        };

        const response = await apiPut(
          `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}`,
          updates
        );

        if (isSuccessResponse(response)) {
          toast.success('Email content saved', {
            description: 'Your changes have been saved successfully.'
          });

          // Invalidate the correspondence query to refetch updated data
          queryClient.invalidateQueries({
            queryKey: ['correspondence', matterId, correspondenceId]
          });

          // Also invalidate the workflow data to ensure it's refreshed
          queryClient.invalidateQueries({
            queryKey: ['workflow-data', matterId, 'follow-up']
          });
        }
      }

      // Call the parent's onSave callback for local state updates
      onSave?.(subject, body);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save email content:', error);
      toast.error('Save failed', {
        description: 'Failed to save your changes. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setUserSubject(null);
    setUserBody(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Compose Email to {institutionName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Email Subject */}
          <div className="space-y-2">
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="w-full"
            />
          </div>

          {/* Email Body */}
          <div className="space-y-2">
            <Label htmlFor="email-body">Message</Label>
            <Textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your email message..."
              className="min-h-[300px] resize-y font-sans"
              rows={15}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            size="xs"
            density="compact"
            gap="standard"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </Button>
          <Button
            size="xs"
            density="compact"
            gap="standard"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-3.5 w-3.5" />
                Save
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
